import { get } from 'lodash';
import { useRouter } from 'next/router';
import { XP_LEVELS } from '../config/kittyLevels';
import { useCitiesStore } from '../hooks/useCitiesStore';
import { useCitizensQuery, useRenterDetailsQuery } from '../hooks/useCitizensQuery';
import { useKittiesStore } from '../hooks/useKittiesStore';
import { useMyCities } from '../hooks/useMyCities';
import { useUserAssetsStore } from '../hooks/useUserAssetsStore';
import { getTreatsEarnedPerDay } from '../utils/calculateClaimableTreats';
import formatNumber from '../utils/formatNumber';
import formatTimeSince from '../utils/formatTimeSince';
import getIpfsImageUrl from '../utils/getIpfsImageUrl';
import { getKittyName } from '../utils/kittyFormatting';
import { KittyProgressBar } from './KittyProgressBar';
import KittySwapHistory from './KittySwapHistory';
import LogoPeraConnect from './LogoPeraConnect';
import NftImage from './NftImage';
import OwnerAddress from './OwnerAddress';
import RemoveFromCityButton from './RemoveFromCityButton';
import Spinner from './Spinner';
import { StatCard } from './StatCard';
import StyledLink from './StyledLink';
import CityImage from './cities/CityImage';

const KittyModalDetail = ({ kitty, collabDetails, onClose }) => {
  const { allCities } = useCitiesStore();

  const router = useRouter();
  const isProfilePage = router.pathname.includes('/profile');
  const assetId = kitty?.index || kitty?.['asset-id'] || kitty?.id || collabDetails?.index;

  const city = allCities.find((c) => (c.citizens || []).includes(assetId) || (c.renters || []).includes(assetId));

  const { myKitties } = useKittiesStore();
  const { checkIfMyCity } = useMyCities();
  const renterDetailsQuery = useRenterDetailsQuery({ city });
  const citizenDetailsQuery = useCitizensQuery({ city });
  const { userAssets } = useUserAssetsStore();

  const citizens = citizenDetailsQuery.data ?? [];
  const renters = renterDetailsQuery.data ?? [];
  const allCitizens = [...citizens, ...renters];

  const isMyCity = checkIfMyCity(city?.index);
  const isMyKitty = myKitties.some((k) => k.id === kitty.id);
  const isRenter = city?.renters?.some((r) => r === kitty.id);
  const citizenDetails = allCitizens.find((c) => c.kitty === assetId);
  const isCollab = !kitty.gen && !!citizenDetails && !!collabDetails;
  const name = isCollab ? collabDetails?.params?.name : getKittyName(kitty);

  const moveInDateSince = citizenDetails?.moveInDate ? formatTimeSince(citizenDetails?.moveInDate.toDate()) : null;
  // const lastClaimedDateSince = citizenDetails?.lastClaimedDate ? formatTimeSince(citizenDetails?.lastClaimedDate) : null;

  const theBio = kitty.bio || {};

  const percentToLevelUp = Math.floor((get(theBio, 'currentExp', 0) / get(theBio, 'nextLevelExp', XP_LEVELS[1])) * 100);
  const kittyLevel = get(theBio, 'level', 1);

  const isMyAsset = userAssets.some((a) => a['asset-id'] === assetId);

  return (
    <div className='mt-6'>
      <div className='flex gap-8 lg:gap-12 flex-col lg:flex-row mb-6'>
        <div className='flex-shrink-0'>
          <div className='relative aspect-square md:w-[500px] md:h-[500px]'>
            <div className='absolute inset-0 w-full h-full flex items-center justify-center'>
              <Spinner className='w-12 h-12' />
            </div>
            <NftImage nft={collabDetails || kitty} className='rounded-lg w-full h-full object-cover absolute' />
          </div>
        </div>

        <div className='w-full'>
          <div className='flex items-center gap-2 mb-4'>
            <p className='text-4xl font-bold text-gray-50 text-left'>{name}</p>
          </div>

          <div className='mb-6 flex gap-4'>
            <StyledLink href={`https://explorer.perawallet.app/asset/${assetId}`} className='flex items-center gap-2' link target='_blank'>
              <div className='w-5 h-5 inline-block'>
                <LogoPeraConnect className='' />
              </div>
              <span>Pera Explorer</span>
            </StyledLink>
            <StyledLink href={`https://www.downbad.farm/asset/${assetId}`} className='flex items-center gap-2' link target='_blank'>
              <img src='/asalytic.png' alt='Asalytic' className='w-5 h-5 inline-block' />
              <span>Asalytic</span>
            </StyledLink>
          </div>

          {!isCollab && (
            <div className='grid grid-cols-1 xl:grid-cols-4 gap-4'>
              {kitty.rank && (
                <StatCard
                  title='Rank'
                  value={`${formatNumber(kitty.rank)}/${
                    kitty.gen === 3 ? '1,069' : kitty.gen === 1 ? 470 : kitty.gen === 2 ? 500 : kitty.gen === 4 ? '2,405' : 0
                  }`}
                />
              )}
              {/* <StatCard title='Level' value={kittyLevel} /> */}
              <StatCard title='Adventures' value={kitty.bio?.stats?.finishedAdventures || 0} />
              <StatCard title='Jobs' value={kitty.bio?.stats?.finishedJobs || 0} />
              <StatCard title='Fight Record' value={`${kitty.bio?.stats?.wonFights || 0} wins`} />
            </div>
          )}

          {kitty.bio?.bio && (
            <div className='bg-white/10 backdrop-blur-sm p-4 rounded-lg mt-4'>
              <div className='mb-2 flex gap-2 items-center'>
                <svg className='w-4 h-4 transform text-primary-500' fill='currentColor' viewBox='0 0 32 32' aria-hidden='true'>
                  <path d='M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z' />
                </svg>
                <h3 className='text-gray-400'>Quote</h3>
              </div>
              <p className='text-lg mb-1 italic'>{kitty.bio?.bio?.quote || `Heyo, I'm ${kitty.bio?.bio?.name}! 👋`}</p>
            </div>
          )}

          {!isCollab && (
            <div className='mt-4'>
              <div className='flex items-center gap-2 mb-2'>
                <h2 className='text-gray-400'>Level Progress</h2>
                <StyledLink href='https://docs.shittykitties.art/website-utilities/shitty-kitty-levels' target='_blank'>
                  How to gain XP?
                </StyledLink>
              </div>
              <KittyProgressBar percentToLevelUp={percentToLevelUp} theBio={theBio} height={30} />
            </div>
          )}

          {!isProfilePage && (
            <div className='inline-flex justify-between mt-4'>
              <OwnerAddress kittyId={assetId} className='!mt-0' />
            </div>
          )}
        </div>
      </div>

      {kitty?.gen === 4 && (
        <div className='my-4 flex flex-col lg:flex-row gap-4'>
          <KittySwapHistory assetId={assetId} />
        </div>
      )}

      {!!city && (
        <div className='bg-gray-800 flex flex-col p-4 rounded-lg'>
          <div className='flex items-center gap-2 text-gray-400 mb-4'>
            <div className='w-5'>
              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 576 512'>
                <path
                  d='M408 120c0 54.6-73.1 151.9-105.2 192c-7.7 9.6-22 9.6-29.6 0C241.1 271.9 168 174.6 168 120C168 53.7 221.7 0 288 0s120 53.7 120 120zm8 80.4c3.5-6.9 6.7-13.8 9.6-20.6c.5-1.2 1-2.5 1.5-3.7l116-46.4C558.9 123.4 576 135 576 152l0 270.8c0 9.8-6 18.6-15.1 22.3L416 503l0-302.6zM137.6 138.3c2.4 14.1 7.2 28.3 12.8 41.5c2.9 6.8 6.1 13.7 9.6 20.6l0 251.4L32.9 502.7C17.1 509 0 497.4 0 480.4L0 209.6c0-9.8 6-18.6 15.1-22.3l122.6-49zM327.8 332c13.9-17.4 35.7-45.7 56.2-77l0 249.3L192 449.4 192 255c20.5 31.3 42.3 59.6 56.2 77c20.5 25.6 59.1 25.6 79.6 0zM288 152a40 40 0 1 0 0-80 40 40 0 1 0 0 80z'
                  fill='currentColor'
                />
              </svg>
            </div>
            <div className='flex items-center gap-2'>
              <h2 className='text-gray-400 font-medium text-lg'>Lives in {city?.details?.name || city?.params?.name}</h2>
              <StyledLink href={`/cities/${city?.index}`} link>
                View City
              </StyledLink>
            </div>
          </div>

          <div className='flex flex-col gap-4 mb-4'>
            <div className='rounded-lg overflow-hidden bg-gray-900 w-full'>
              <CityImage imageUrl={getIpfsImageUrl(city.cid, { width: 2000, quality: 95 })} />
            </div>

            <div className='flex flex-col md:flex-row gap-4'>
              {city && !isProfilePage && <KittyModalDetailTreats kitty={kitty} city={city} />}

              <div className='flex flex-col gap-4 flex-1'>
                <StatCard title='Moved in' value={`${moveInDateSince} ago`} />
              </div>
            </div>

            {(isMyAsset || isMyKitty) && !!city && !isProfilePage && (
              <div className='flex justify-center'>
                <RemoveFromCityButton assetId={assetId} isRenter={isRenter} isMyCity={isMyCity} isMyKitty={isMyKitty} onRemove={onClose} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const KittyModalDetailTreats = ({ kitty, city }) => {
  const { treatsPerDay, bonusTreats } = getTreatsEarnedPerDay(kitty, city);

  return (
    <div className='bg-white/10 backdrop-blur-sm p-4 rounded-lg flex-1'>
      <h3 className='text-gray-400 mb-2'>Daily Treats</h3>
      <p className='text-lg mb-1'>
        Earns {treatsPerDay} {treatsPerDay === 1 ? 'treat' : 'treats'} <span className='text-gray-400 text-sm'>per day for city owner</span>
      </p>
      {typeof bonusTreats === 'number' && (
        <p className='text-lg'>
          +{bonusTreats} treats <span className='text-gray-400 text-sm'>based on Shitty City Rating</span>
        </p>
      )}
    </div>
  );
};

export default KittyModalDetail;
